import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, FileText, CheckCircle, Trash2, Plus, Loader2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { uploadFiles, FileToUpload } from '../ScanningSlice';

interface PdfPreviewModalProps {
    isOpen: boolean;
    files: File[];
    onClose: () => void;
    onBack: () => void;
    subjectName?: string;
    subjectCode?: string;
    onUploaded?: () => void;
}

export const PdfPreviewModal: React.FC<PdfPreviewModalProps> = ({
    isOpen,
    files,
    onClose,
    subjectName = 'Scanned Document',
    subjectCode = 'SCAN-001',
    onUploaded
}) => {
    const dispatch = useAppDispatch();
    const { isUploading, uploadProgress } = useAppSelector(
        (state) => state.scanning
    );

    const [submitError, setSubmitError] = useState<string | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>(files);
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    // File input ref for adding more files
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Sync selectedFiles with incoming files prop when modal opens
    useEffect(() => {
        if (isOpen && files.length > 0) {
            setSelectedFiles(files);
        }
    }, [isOpen, files]);

    // Create object URL for preview - same as image preview
    useEffect(() => {
        if (previewFile) {
            const url = URL.createObjectURL(previewFile);
            setPreviewUrl(url);
            return () => {
                URL.revokeObjectURL(url);
            };
        } else {
            setPreviewUrl(null);
        }
    }, [previewFile]);

    const handleFileClick = (file: File) => {
        setPreviewFile(file);
    };

    const handleClosePreview = () => {
        setPreviewFile(null);
    };

    const handleRemoveFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleAddMoreFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newFiles = event.target.files;
        console.log('Add More Files triggered:', newFiles);

        if (!newFiles || newFiles.length === 0) {
            console.log('No files selected');
            return;
        }

        // Close preview if open
        setPreviewFile(null);

        // Add new files to existing files
        const filesArray = Array.from(newFiles);
        console.log('New files to add:', filesArray);
        console.log('Current files:', selectedFiles);

        setSelectedFiles(prev => {
            const updated = [...filesArray, ...prev];
            console.log('Updated files:', updated);
            return updated;
        });

        // Reset input value to allow selecting the same file again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Drag and Drop handlers
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const newFiles = Array.from(e.dataTransfer.files);
            const validFiles = newFiles.filter(file => {
                const extension = file.name.split('.').pop()?.toLowerCase();
                return ['pdf', 'ppt', 'pptx'].includes(extension || '') ||
                    file.type === 'application/pdf' ||
                    file.type.includes('presentation');
            });

            if (validFiles.length > 0) {
                setSelectedFiles(prev => [...validFiles, ...prev]);
                setPreviewFile(null);
            }
        }
    };

    const handleSubmit = async () => {
        setSubmitError(null);
        try {
            // Prepare files for upload
            const filesToUpload: FileToUpload[] = selectedFiles.map(file => ({
                file_name: file.name,
                file_type: file.type,
                file: file,
            }));

            // Upload using Redux action
            await dispatch(
                uploadFiles({
                    subject_name: subjectName,
                    paper_code: subjectCode,
                    files: filesToUpload,
                })
            ).unwrap();

            // Notify caller so it can refresh (e.g. answer-sheet list), then close
            onUploaded?.();

            // Success - Close modal immediately to show main SuccessModal from parent
            onClose();

        } catch (error: any) {
            setSubmitError(error || 'Failed to complete upload process');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 md:p-4">
            {/* Toast Notifications */}
            <div className="absolute top-3 md:top-6 left-1/2 -translate-x-1/2 z-[110] w-full max-w-md px-3 md:px-4 flex flex-col gap-2">
                {submitError && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="bg-red-500 text-white px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl shadow-lg flex items-center justify-between border border-red-600"
                    >
                        <span className="font-medium text-xs md:text-sm">{submitError}</span>
                        <button onClick={() => setSubmitError(null)} className="p-1 hover:bg-white/20 rounded-full transition-colors ml-2 shrink-0">
                            <X className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}

            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-card w-full max-w-4xl h-[100dvh] md:h-[90vh] rounded-none md:rounded-2xl flex flex-col overflow-hidden shadow-2xl border-0 md:border border-border relative"
            >
                {/* Header */}
                <div className="p-3 md:p-6 border-b border-border flex justify-between items-start bg-card shrink-0">
                    <div className="flex-1 min-w-0 pr-2">
                        <h2 className="text-lg md:text-2xl font-bold flex items-center gap-2">
                            <FileText className="w-5 h-5 md:w-6 md:h-6 text-red-500 shrink-0" />
                            <span className="truncate">Review PDF Files</span>
                        </h2>
                        <p className="text-muted-foreground text-xs md:text-sm mt-1">
                            {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} ready for upload
                        </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {/* Add More Files Button */}
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading}
                            className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-primary bg-primary/5 hover:bg-primary/10 active:bg-primary/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                            title="Add more files"
                        >
                            <Plus className="w-3.5 h-3.5 md:w-4 md:h-4" />
                            <span>Add More</span>
                        </button>

                        {/* Hidden File Input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                            multiple
                            className="hidden"
                            onChange={handleAddMoreFiles}
                        />

                        {/* Close Button */}
                        <button onClick={onClose} className="p-2 hover:bg-secondary rounded-full transition-colors">
                            <X className="w-5 h-5 md:w-6 md:h-6" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div
                    className="flex-1 overflow-y-auto p-3 md:p-6 bg-secondary/10 relative"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >

                    {/* PDF Preview Overlay - Simple like Image Preview */}
                    {previewFile && previewUrl && (
                        <div className="absolute inset-0 z-50 bg-zinc-900/95 flex flex-col animate-in fade-in duration-200">
                            <div className="flex justify-between items-center text-white p-3 md:p-4 shrink-0 bg-black/40 backdrop-blur-md border-b border-white/10">
                                <h3 className="text-base md:text-lg font-bold flex items-center gap-2 flex-1 min-w-0 pr-2">
                                    <FileText className="w-4 h-4 md:w-5 md:h-5 text-red-500 shrink-0" />
                                    <span className="truncate">{previewFile.name}</span>
                                </h3>
                                <button
                                    onClick={handleClosePreview}
                                    className="bg-white/10 p-2 rounded-full hover:bg-white/20 transition-colors shrink-0"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-auto bg-zinc-900 p-2 md:p-4">
                                <div className="w-full h-full min-h-[500px] bg-white rounded-lg shadow-2xl">
                                    <embed
                                        src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                                        type="application/pdf"
                                        className="w-full h-full min-h-[500px]"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedFiles.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-muted-foreground border-2 border-dashed border-border rounded-xl p-4 text-center text-sm">
                            No files selected. Go back to upload.
                        </div>
                    ) : (
                        <div className="space-y-2 md:space-y-3">
                            {selectedFiles.map((file, index) => (
                                <div
                                    key={`${file.name}-${index}`}
                                    onClick={() => handleFileClick(file)}
                                    className="bg-card border border-border rounded-xl p-3 md:p-4 flex items-center gap-3 md:gap-4 hover:shadow-md hover:border-primary/50 active:scale-[0.98] transition-all group cursor-pointer"
                                >
                                    {/* File Icon */}
                                    <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center shrink-0">
                                        <FileText className="w-5 h-5 md:w-6 md:h-6 text-red-600 dark:text-red-400" />
                                    </div>

                                    {/* File Info */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-sm md:text-base truncate">{file.name}</h3>
                                        <p className="text-xs text-muted-foreground">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type.split('/')[1]?.toUpperCase() || 'PDF'}
                                        </p>
                                        <p className="text-xs text-primary mt-0.5 md:mt-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                            Tap to preview
                                        </p>
                                    </div>

                                    {/* Remove Button */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveFile(index);
                                        }}
                                        disabled={isUploading}
                                        className={`w-9 h-9 md:w-9 md:h-9 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white rounded-lg flex items-center justify-center transition-colors shrink-0 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        title="Remove file"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-3 md:p-6 border-t border-border bg-card flex flex-col-reverse md:flex-row justify-between items-stretch md:items-center gap-2 md:gap-4 shrink-0">
                    <button
                        onClick={onClose}
                        className="w-full md:w-auto px-6 py-3 rounded-lg md:rounded-xl border border-border font-medium hover:bg-secondary active:bg-secondary/80 transition-colors text-sm"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={isUploading || selectedFiles.length === 0}
                        className="w-full md:w-auto px-8 py-3 bg-primary text-primary-foreground font-bold rounded-lg md:rounded-xl shadow-lg shadow-primary/20 hover:shadow-primary/40 active:scale-95 transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isUploading ? (
                            <div className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                                <span>Uploading... {uploadProgress}%</span>
                            </div>
                        ) : (
                            <>
                                <CheckCircle className="w-4 h-4 md:w-5 md:h-5" />
                                Submit {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
                            </>
                        )}
                    </button>
                </div>
            </motion.div>
        </div>
    );
};
